import { useEffect, useMemo, useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Tooltip } from '../../../../components/Tooltip';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdInput, TdSelect } from '../../../../components/inline-add-table/cells';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { TextField } from '../../../../components/inputs/TextField';
import { useToast } from '../../../../components/toastContext';
import { announceAttritionCategoriesChanged } from '../../../../lib/navEvents';
import { ApiError } from '../../../../lib/apiError';
import { uid } from '../../../../lib/uid';
import { focusValidationError } from '../../../../lib/validationFocus';
import type { OrgUnitOption } from '../../organization/organizationTypes';
import { useRecruitmentUsers } from '../../vacancies/useRecruitmentUsers';
import { attritionCategoriesApi } from '../attritionCategoriesApi';
import type { AttritionCategoryDetail } from '../attritionCategoryTypes';
import { sampleAttritionCategory } from '../utils/sampleAttritionCategory';

const ID_PREFIX = 'attrition-category';

/** `reviewerUserId` to `reviewer-user-id` — exactly as `focusValidationError` does it. */
const kebab = (field: string) => field.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

/**
 * The id of one inline cell — `attrition-category-documents.2.reviewer-user-id`. A CONTRACT with
 * the server, which names a rejected line `documents.2.reviewerUserId` (`clearances.2.orgUnitId`).
 */
const cellId = (list: 'documents' | 'clearances', index: number, field: string) =>
  `${ID_PREFIX}-${list}.${index}.${kebab(field)}`;

interface DraftLine {
  key: string;
  /** The line's own id when it is already on the checklist — what keeps the filed requests pointing at it. */
  id: string | null;
  name: string;
  reviewerUserId: string | null;
  description: string;
}

/** One clearance row. No id: the UNIT is the line's key on the server. */
interface DraftClearance {
  key: string;
  orgUnitId: string | null;
  signatoryUserId: string | null;
}

const EMPTY = (): DraftLine => ({ key: uid(), id: null, name: '', reviewerUserId: null, description: '' });

const EMPTY_CLEARANCE = (): DraftClearance => ({ key: uid(), orgUnitId: null, signatoryUserId: null });

/** A row nobody touched — the server's `IsBlank`. */
const isBlank = (line: DraftLine) => line.name.trim() === '' && line.reviewerUserId == null && line.description.trim() === '';

/** A row somebody finished: the document named, and somebody to review it. */
const isFilled = (line: DraftLine) => line.name.trim() !== '' && line.reviewerUserId != null;

const clearanceBlank = (line: DraftClearance) => line.orgUnitId == null && line.signatoryUserId == null;

const clearanceFilled = (line: DraftClearance) => line.orgUnitId != null && line.signatoryUserId != null;

const linesOf = (editing: AttritionCategoryDetail | null): DraftLine[] =>
  editing != null && editing.documents.length > 0
    ? editing.documents.map((d) => ({
        key: uid(),
        id: d.id,
        name: d.name,
        reviewerUserId: d.reviewerUserId,
        description: d.description ?? '',
      }))
    : [EMPTY()];

const clearancesOf = (editing: AttritionCategoryDetail | null): DraftClearance[] =>
  editing != null && editing.clearances.length > 0
    ? editing.clearances.map((c) => ({ key: uid(), orgUnitId: c.orgUnitId, signatoryUserId: c.signatoryUserId }))
    : [EMPTY_CLEARANCE()];

/**
 * What the modal writes. A CREATE writes the category, its first checklist and its clearances in
 * one save; on a record, **each section has its own edit and its own write** — Details (name,
 * description) on the Info tab, the checklist and the clearances each on their own tab — and no
 * form can reach another's fields.
 */
export type AttritionCategorySection = 'create' | 'details' | 'checklist' | 'clearances';

/**
 * Create an attrition category, or edit one section of it.
 *
 * **The checklist is LINES, each naming the account that reviews that document** (product call,
 * 29 Oct 2026). A line already on the checklist keeps its id, so the requests filed against it keep
 * pointing at it; one removed here is taken off for new requests and stays on the ones already filed.
 *
 * **The clearances are OPTIONAL — org units, each with the account that signs for it** (product
 * call, 03 Nov 2026). A unit has no head on the chart, so the signatory is named here.
 *
 * **Both pickers are this module's own doors** (`attrition-categories/user-options`,
 * `…/unit-options`), so an administrator needs no Users or Organization key to fill them.
 */
export function AttritionCategoryModal({
  open,
  section,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  section: AttritionCategorySection;
  /** The record a section edit works on. Null on a create. */
  editing: AttritionCategoryDetail | null;
  onClose: () => void;
  /** `keepOpen` after a create with Add another on — the parent refreshes and leaves the form up. */
  onSaved: (saved: AttritionCategoryDetail, options?: { keepOpen?: boolean }) => void;
}) {
  const toast = useToast();
  const creating = section === 'create';
  const showDetails = section === 'create' || section === 'details';
  const showChecklist = section === 'create' || section === 'checklist';
  const showClearances = section === 'create' || section === 'clearances';

  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [lines, setLines] = useState<DraftLine[]>(() => linesOf(editing));
  const [clearances, setClearances] = useState<DraftClearance[]>(() => clearancesOf(editing));

  // The account feed is fetched only where a reviewer or a signatory is picked.
  const loadUsers = showChecklist || showClearances ? attritionCategoriesApi.userOptions : NO_USERS;
  const { users, loading: loadingUsers, error: usersError } = useRecruitmentUsers(loadUsers);

  const [units, setUnits] = useState<OrgUnitOption[]>([]);
  // Seeded from whether the feed is wanted: the fetch starts after paint, so `false` would flash the form.
  const [loadingUnits, setLoadingUnits] = useState(showClearances);
  const [unitsError, setUnitsError] = useState(false);

  useEffect(() => {
    if (!showClearances) return;

    const controller = new AbortController();
    attritionCategoriesApi
      .unitOptions(controller.signal)
      .then((loaded) => {
        setUnits(loaded);
        setLoadingUnits(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setLoadingUnits(false);
        setUnitsError(true);
      });

    return () => controller.abort();
  }, [showClearances]);

  const [addAnother, setAddAnother] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    THE CURRENT ACCOUNT STAYS OFFERED on an edit — the feed carries live accounts only, so a
    reviewer or signatory since deactivated would otherwise leave the cell holding an id it cannot show.
  */
  const accountOptions = useMemo(() => {
    const live = users.map((u) => ({ value: u.userId, label: u.displayName }));
    const current = [
      ...(editing?.documents ?? []).map((d) => ({ value: d.reviewerUserId, label: d.reviewerName })),
      ...(editing?.clearances ?? []).map((c) => ({ value: c.signatoryUserId, label: c.signatoryName })),
    ];
    const kept = current
      .filter((o) => !live.some((l) => l.value === o.value))
      .map((o) => ({ value: o.value, label: o.label ?? 'Current account', sublabel: 'Account deactivated' }));

    return [...live, ...kept.filter((o, i) => kept.findIndex((k) => k.value === o.value) === i)];
  }, [users, editing]);

  // A unit since deleted from the chart stays offered on the line that already names it.
  const unitOptions = useMemo(() => {
    const live = units.map((u) => ({ value: u.id, label: u.name, sublabel: u.orgUnitTypeName || undefined }));
    const kept = (editing?.clearances ?? [])
      .filter((c) => !live.some((l) => l.value === c.orgUnitId))
      .map((c) => ({ value: c.orgUnitId, label: c.orgUnitName, sublabel: 'Deleted from the chart' }));

    return [...live, ...kept];
  }, [units, editing]);

  const filledCount = lines.filter(isFilled).length;
  const linesValid = filledCount > 0 && lines.every((l) => isFilled(l) || isBlank(l));
  const clearancesValid = clearances.every((c) => clearanceFilled(c) || clearanceBlank(c));
  const valid =
    (!showDetails || name.trim().length > 0) && (!showChecklist || linesValid) && (!showClearances || clearancesValid);
  const canSave = valid && confirmed && !saving;

  const saveDisabledReason = saving
    ? null
    : showDetails && name.trim() === ''
      ? 'Name the category.'
      : showChecklist && filledCount === 0
        ? 'Add at least one document, with the account that reviews it.'
        : showChecklist && !linesValid
          ? 'Every document needs a name and a reviewer. Remove the unfinished one, or fill it in.'
          : showClearances && !clearancesValid
            ? 'Every clearance needs a unit and a signatory. Remove the unfinished one, or fill it in.'
            : !confirmed
              ? 'Tick the confirm box to submit.'
              : null;

  function patch(key: string, change: Partial<DraftLine>) {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...change } : line)));
  }

  function patchClearance(key: string, change: Partial<DraftClearance>) {
    setClearances((current) => current.map((line) => (line.key === key ? { ...line, ...change } : line)));
  }

  // EVERY row is sent, blanks included, so a rejection's index is the row on screen.
  const documentsBody = () =>
    lines.map((l) => ({
      id: l.id,
      name: l.name.trim(),
      description: l.description.trim() || null,
      reviewerUserId: l.reviewerUserId,
    }));

  const clearancesBody = () => clearances.map((c) => ({ orgUnitId: c.orgUnitId, signatoryUserId: c.signatoryUserId }));

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const saved =
        section === 'create'
          ? await attritionCategoriesApi.create({
              name: name.trim(),
              description: description.trim() || null,
              documents: documentsBody(),
              clearances: clearancesBody(),
            })
          : section === 'details'
            ? await attritionCategoriesApi.update(editing!.id, { name: name.trim(), description: description.trim() || null })
            : section === 'checklist'
              ? await attritionCategoriesApi.updateChecklist(editing!.id, { documents: documentsBody() })
              : await attritionCategoriesApi.updateClearances(editing!.id, { clearances: clearancesBody() });

      announceAttritionCategoriesChanged();

      toast.success(
        section === 'create'
          ? `${saved.name} was created.`
          : section === 'details'
            ? `${saved.name} was updated.`
            : section === 'checklist'
              ? `${saved.name}'s checklist was updated.`
              : `${saved.name}'s clearances were updated.`,
      );

      if (creating && addAnother) {
        // A category's identity is all of it — nothing carries to the next one but the toggle.
        setName('');
        setDescription('');
        setLines([EMPTY()]);
        setClearances([EMPTY_CLEARANCE()]);
        setConfirmed(false);
        onSaved(saved, { keepOpen: true });
      } else {
        onSaved(saved);
      }
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save this category.', 0);

      setError(failure);
      focusValidationError(failure, {
        idPrefix: ID_PREFIX,
        // The refusals naming the WHOLE checklist — none at all, the cap — are answered at the `+`.
        fieldIds: { documents: `${ID_PREFIX}-add-document`, clearances: `${ID_PREFIX}-add-clearance` },
      });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleAttritionCategory(users, units);

    // A section edit fills its own fields only, and never renames a record the reader opened.
    if (creating) setName(sample.name);
    if (showDetails) setDescription(sample.description);
    if (showChecklist) {
      setLines(
        sample.documents.map((d) => ({
          key: uid(),
          id: null,
          name: d.name,
          reviewerUserId: d.reviewerUserId,
          description: d.description,
        })),
      );
    }
    if (showClearances) {
      setClearances(
        sample.clearances.length > 0
          ? sample.clearances.map((c) => ({ key: uid(), orgUnitId: c.orgUnitId, signatoryUserId: c.signatoryUserId }))
          : [EMPTY_CLEARANCE()],
      );
    }
  }

  const title =
    section === 'create'
      ? 'New category'
      : section === 'details'
        ? `Edit ${editing!.name}`
        : section === 'checklist'
          ? `Edit checklist — ${editing!.name}`
          : `Edit clearances — ${editing!.name}`;

  const feedsLoading = ((showChecklist || showClearances) && loadingUsers) || (showClearances && loadingUnits);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size={showDetails && !creating ? 'lg' : '2xl'}
      id={`${ID_PREFIX}-modal`}
      loading={feedsLoading}
      autogenerate={{ onGenerate: generate, disabled: saving || feedsLoading }}
      addAnother={
        creating ? { id: `${ID_PREFIX}-add-another`, checked: addAnother, onChange: setAddAnother, disabled: saving } : undefined
      }
      confirm={{
        id: `${ID_PREFIX}-confirm`,
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip disabledReason={saveDisabledReason}>
            <Button
              id={`${ID_PREFIX}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {(showChecklist || showClearances) && usersError ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">
              The accounts could not be loaded, so no reviewer or signatory can be chosen. Close this form and open it
              again.
            </p>
          </div>
        ) : null}

        {showClearances && unitsError ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">
              The org units could not be loaded, so no clearance can be added. Close this form and open it again.
            </p>
          </div>
        ) : null}

        {showDetails ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id={`${ID_PREFIX}-name`}
              label="Name"
              required
              description="The kind of separation — Retirement, Resignation, Illness."
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={name.trim() ? error?.fieldError('name') : 'Name the category.'}
              disabled={saving}
            />

            <TextField
              id={`${ID_PREFIX}-description`}
              label="Description"
              description="One line saying when this category applies. Shown to whoever files a request."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              error={error?.fieldError('description')}
              disabled={saving}
            />
          </div>
        ) : null}

        {showChecklist ? (
          <>
            <FormFieldset legend="Document checklist">
              <InlineAddTable
                headers={['Document *', 'Reviewer *', 'Description']}
                colWidths={[240, 220, 300]}
                resizeKey={`${ID_PREFIX}-documents-v1`}
                flexColumn={2}
                addButtonId={`${ID_PREFIX}-add-document`}
                addDisabled={saving || lines.some((l) => !isFilled(l))}
                addDisabledReason="Finish the current document before adding another."
                rowCount={lines.length}
                onAddRow={() => setLines((current) => [...current, EMPTY()])}
              >
                {lines.map((line, index) => (
                  <InlineRow
                    key={line.key}
                    idx={index + 1}
                    onRemove={() => setLines((current) => current.filter((l) => l.key !== line.key))}
                  >
                    <TdInput
                      id={cellId('documents', index, 'name')}
                      ariaLabel={`Document, line ${index + 1}`}
                      value={line.name}
                      onChange={(next) => patch(line.key, { name: next })}
                      placeholder="Clearance"
                      disabled={saving}
                    />
                    <TdSelect
                      id={cellId('documents', index, 'reviewerUserId')}
                      ariaLabel={`Reviewer, line ${index + 1}`}
                      value={line.reviewerUserId}
                      onChange={(next) => patch(line.key, { reviewerUserId: next || null })}
                      options={accountOptions}
                      remote
                      disabled={saving}
                    />
                    <TdInput
                      id={cellId('documents', index, 'description')}
                      ariaLabel={`Description, line ${index + 1}`}
                      value={line.description}
                      onChange={(next) => patch(line.key, { description: next })}
                      disabled={saving}
                    />
                  </InlineRow>
                ))}
              </InlineAddTable>

              {error?.fieldError('documents') ? (
                <p className="text-xs text-red-700">{error.fieldError('documents')}</p>
              ) : null}
            </FormFieldset>

            {/*
              The CONSEQUENCE, on the form where it is read before the tick: the checklist is
              copied onto a request when it is filed, and the reviewer is read live.
            */}
            <p className="text-xs text-slate-500">
              A request filed under this category asks for every document on the checklist as it stands when the
              request is filed. Changing a reviewer moves the documents already waiting on them; removing a document
              leaves it on the requests already filed.
            </p>
          </>
        ) : null}

        {showClearances ? (
          <>
            <FormFieldset legend="Clearances">
              <InlineAddTable
                headers={['Org unit *', 'Signatory *']}
                colWidths={[320, 320]}
                resizeKey={`${ID_PREFIX}-clearances-v1`}
                flexColumn={1}
                addButtonId={`${ID_PREFIX}-add-clearance`}
                addDisabled={saving || clearances.some((c) => !clearanceFilled(c))}
                addDisabledReason="Finish the current clearance before adding another."
                rowCount={clearances.length}
                onAddRow={() => setClearances((current) => [...current, EMPTY_CLEARANCE()])}
              >
                {clearances.map((line, index) => (
                  <InlineRow
                    key={line.key}
                    idx={index + 1}
                    onRemove={() => setClearances((current) => current.filter((c) => c.key !== line.key))}
                  >
                    <TdSelect
                      id={cellId('clearances', index, 'orgUnitId')}
                      ariaLabel={`Org unit, line ${index + 1}`}
                      value={line.orgUnitId}
                      onChange={(next) => patchClearance(line.key, { orgUnitId: next || null })}
                      // A unit already on another row is not offered twice — the server would collapse it.
                      options={unitOptions.filter(
                        (o) => o.value === line.orgUnitId || !clearances.some((c) => c.orgUnitId === o.value),
                      )}
                      remote
                      disabled={saving}
                    />
                    <TdSelect
                      id={cellId('clearances', index, 'signatoryUserId')}
                      ariaLabel={`Signatory, line ${index + 1}`}
                      value={line.signatoryUserId}
                      onChange={(next) => patchClearance(line.key, { signatoryUserId: next || null })}
                      options={accountOptions}
                      remote
                      disabled={saving}
                    />
                  </InlineRow>
                ))}
              </InlineAddTable>
            </FormFieldset>

            <p className="text-xs text-slate-500">
              Optional. Each unit listed must clear the employee before a request filed under this category can be
              completed, and its signatory is the one account that can clear it. A request keeps the units as they stand
              when it is filed; changing a signatory moves the clearances already waiting on them.
            </p>
          </>
        ) : null}
      </div>
    </Modal>
  );
}

/** The account feed a Details edit never asks for — stable, so the hook does not refetch. */
const NO_USERS = (): Promise<[]> => Promise.resolve([]);
